/**
 * 聊天加载动画组件
 * 用于显示 AI 正在思考/加载的状态
 */

import React, { useEffect, useRef } from 'react';
import { View, Animated } from 'react-native';
import { styles } from './styles';
import type { DbChatLoadingProps } from './types';

/**
 * 加载动画 - 三个点依次闪烁
 */
const DbChatLoading: React.FC<DbChatLoadingProps> = ({
    dotCount = 3,
    dotSize = 6,
    dotColor = '#999',
    duration = 1200,
}) => {
    const animatedValues = useRef(
        Array.from({ length: dotCount }, () => new Animated.Value(0.3)),
    ).current;

    useEffect(() => {
        const animations = animatedValues.map((animatedValue, index) =>
            Animated.sequence([
                Animated.delay(index * (duration / dotCount)),
                Animated.loop(
                    Animated.sequence([
                        Animated.timing(animatedValue, {
                            toValue: 1,
                            duration: duration / dotCount,
                            useNativeDriver: true,
                        }),
                        Animated.timing(animatedValue, {
                            toValue: 0.3,
                            duration: duration / dotCount,
                            useNativeDriver: true,
                        }),
                    ]),
                ),
            ]),
        );

        Animated.parallel(animations).start();

        return () => {
            animatedValues.forEach(value => value.setValue(0.3));
        };
    }, [animatedValues, duration, dotCount]);

    return (
        <View style={styles.container}>
            {animatedValues.map((animatedValue, index) => (
                <Animated.View
                    key={index}
                    style={[
                        styles.dot,
                        {
                            width: dotSize,
                            height: dotSize,
                            borderRadius: dotSize / 2,
                            backgroundColor: dotColor,
                            opacity: animatedValue,
                            marginHorizontal: 2,
                        },
                    ]}
                />
            ))}
        </View>
    );
};

DbChatLoading.displayName = 'DbChatLoading';

export default DbChatLoading;
export type { DbChatLoadingProps } from './types';
